import React from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { ReactComponent as UserIcon } from '../../../../assets/icons/svg/userIcon.svg';
import { PromotionDiscountRows } from '../../../../utils/promotionSummary';
import PassengerEnglishName from '../../PassengerEnglishName';
import '../../../../css/NavigatorBar/NavigatorContents/navPassengerContent.css'

const getExtraGuestNo = (extra) => String(extra?.guest_no ?? '').trim();
const getExtraLabel = (extra) => {
  const codeLabel = extra?.code ? String(extra.code).trim() : '';
  const description = extra?.description ? String(extra.description).trim() : '';
  return [codeLabel, description].filter(Boolean).join(' - ');
};
const getPassengerName = (passenger) => String(passenger?.name ?? '').trim();

// 승객별 요금 내역을 아코디언으로 보여주는 탭 컴포넌트
function NavPassengerContent({ passengers, itemInfo, lineId }) { // passengers와 itemInfo를 직접 받습니다.
  console.log('NavPassengerContent received passengers:', passengers);
  console.log('NavPassengerContent received itemInfo:', itemInfo);

  const passengerData = React.useMemo(
    () => (Array.isArray(passengers) ? passengers : []),
    [passengers]
  );
  const hasPassengerData = passengerData.length > 0;
  const spriceTotal = Number(itemInfo?.spriceTotal ?? 0);
  const paymentSummary = itemInfo?.paymentSummary ?? null;
  const paymentReceipts = React.useMemo(
    () => (Array.isArray(itemInfo?.paymentReceipts) ? itemInfo.paymentReceipts : []),
    [itemInfo?.paymentReceipts]
  );
  const promotions = Array.isArray(itemInfo?.promotions) ? itemInfo.promotions : [];
  const extras = React.useMemo(
    () => (Array.isArray(itemInfo?.extras) ? itemInfo.extras : []),
    [itemInfo?.extras]
  );
  const onboardCredit = itemInfo?.onboardCredit ?? null;
  const isCanceled = itemInfo?.status === 'cancel';
  const isMscLine = Number(itemInfo?.lineId ?? lineId ?? 0) === 16;
  const showPaymentSummary = !isCanceled && itemInfo;

  const parseAmount = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const normalized = typeof value === 'string' ? value.replace(/,/g, '') : value;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const formatCurrency = (value, currency = 'USD') => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    try {
      return numeric.toLocaleString('en-US', { style: 'currency', currency: currency || 'USD' });
    } catch (error) {
      return numeric.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }
  };
  const formatDueLabel = (dateValue) => {
    if (!dateValue) return '-';
    const normalized = String(dateValue).slice(0, 10);
    return `${normalized}까지`;
  };
  const extrasByGuest = React.useMemo(() => {
    return extras.reduce((acc, extra) => {
      const guestNo = getExtraGuestNo(extra);
      if (!guestNo) return acc;
      if (!acc[guestNo]) acc[guestNo] = [];
      acc[guestNo].push(extra);
      return acc;
    }, {});
  }, [extras]);
  const passengerNameByGuest = React.useMemo(() => {
    return passengerData.reduce((acc, passenger, idx) => {
      const guestNo = String(idx + 1);
      const name = getPassengerName(passenger);
      if (name) acc[guestNo] = name;
      return acc;
    }, {});
  }, [passengerData]);
  const getExtraPassengerName = React.useCallback((extra) => {
    const guestNo = getExtraGuestNo(extra);
    if (!guestNo) return '';
    return passengerNameByGuest[guestNo] || String(extra?.passenger_name ?? '').trim();
  }, [passengerNameByGuest]);
  const selectedExtraTotal = extras.reduce((sum, extra) => {
    const amount = parseAmount(extra?.price);
    return amount !== null ? sum + amount : sum;
  }, 0);

  // 모든 승객의 total.final 값을 합산합니다.
  const totalFinalSum = passengerData.reduce((sum, item) => sum + (item?.total?.final || 0), 0) + selectedExtraTotal;
  const summaryTotalFallback = Number.isFinite(spriceTotal) && spriceTotal > 0 ? spriceTotal : totalFinalSum;
  // API 요약이 있으면 총액/예약금/잔금을 우선 사용
  const totalAmount = parseAmount(paymentSummary?.total_amount);
  const depositAmount = parseAmount(paymentSummary?.deposit_amount);
  const finalAmount = parseAmount(paymentSummary?.final_amount);
  const finalPaidAmount = parseAmount(paymentSummary?.final_paid_amount);
  const depositPaid = paymentSummary?.deposit_paid === true;
  const finalPaid = paymentSummary?.final_paid === true;
  const depositDueDate = paymentSummary?.deposit_due_date || null;
  const finalDueDate = paymentSummary?.final_due_date || null;
  const summaryTotal = totalAmount !== null ? totalAmount : summaryTotalFallback;
  const displayDeposit = depositAmount !== null ? depositAmount : 0;
  const displayFinal = finalAmount !== null ? finalAmount : Math.max(summaryTotal - displayDeposit, 0);
  const displayFinalPaid = finalPaidAmount !== null ? Math.min(finalPaidAmount, displayFinal) : null;
  const displayFinalRemaining = displayFinalPaid !== null ? Math.max(displayFinal - displayFinalPaid, 0) : null;
  const hasPartialFinalPayment = displayFinal > 0.009
    && !finalPaid
    && displayFinalPaid !== null
    && displayFinalPaid > 0.009
    && displayFinalRemaining !== null
    && displayFinalRemaining > 0.009;
  const finalAmountLabel = hasPartialFinalPayment
    ? `${formatCurrency(displayFinal)} (${formatCurrency(displayFinalPaid)} 결제완료 / 잔여 ${formatCurrency(displayFinalRemaining)})`
    : formatCurrency(displayFinal);
  const depositStatusLabel = displayDeposit > 0
    ? (depositPaid ? '결제완료' : formatDueLabel(depositDueDate))
    : '';
  const finalStatusLabel = displayFinal > 0
    ? (finalPaid ? '결제완료' : formatDueLabel(finalDueDate))
    : '';
  const showBreakdown = !isCanceled && (summaryTotal > 0 || displayDeposit > 0 || displayFinal > 0);

  // 영수증 합계 및 표시용 보정 (예약금/잔금 모두 결제완료이고 합계가 부족한 경우, 마지막 잔금 또는 전액 결제 금액을 가산)
  const paidTotal = paymentReceipts.reduce((sum, receipt) => {
    const v = parseAmount(receipt?.payment_amount_usd);
    return v !== null ? sum + v : sum;
  }, 0);

  const paymentReceiptsForView = React.useMemo(() => {
    if (!isMscLine || !(depositPaid && finalPaid) || !Array.isArray(paymentReceipts) || paymentReceipts.length === 0) return paymentReceipts;
    const diff = summaryTotal - paidTotal;
    if (!(Number.isFinite(diff) && diff > 0.009)) return paymentReceipts; // 1센트 이하 무시

    const findLastIdxByType = (types) => {
      for (let i = paymentReceipts.length - 1; i >= 0; i--) {
        const t = String(paymentReceipts[i]?.payment_type || '').toUpperCase();
        if (types.includes(t)) return i;
      }
      return -1;
    };

    let targetIdx = findLastIdxByType(['F']);
    if (targetIdx === -1) targetIdx = findLastIdxByType(['T']);
    if (targetIdx === -1) targetIdx = paymentReceipts.length - 1;
    if (targetIdx < 0) return paymentReceipts;

    const cloned = paymentReceipts.map(r => ({ ...r }));
    const base = parseAmount(cloned[targetIdx]?.payment_amount_usd) || 0;
    cloned[targetIdx].payment_amount_usd_adjusted = base + diff;
    return cloned;
  }, [isMscLine, depositPaid, finalPaid, paymentReceipts, summaryTotal, paidTotal]);
  const visiblePaymentReceiptsForView = React.useMemo(() => (
    paymentReceiptsForView.filter((receipt) => {
      const amountVal = parseAmount(receipt?.payment_amount_usd_adjusted ?? receipt?.payment_amount_usd);
      return amountVal !== null && Math.abs(amountVal) > 0.009;
    })
  ), [paymentReceiptsForView]);

  return (
    <div>
      <Accordion defaultActiveKey="0" flush> {/* defaultActiveKey로 첫 번째 아코디언을 열어둡니다. */}
        {hasPassengerData ? passengerData.map((item, idx) => {
          const guestNo = String(idx + 1);
          const passengerExtras = extrasByGuest[guestNo] || [];
          const passengerExtraTotal = passengerExtras.reduce((sum, extra) => {
            const amount = parseAmount(extra?.price);
            return amount !== null ? sum + amount : sum;
          }, 0);
          const passengerExtraCurrency = passengerExtras.find(extra => parseAmount(extra?.price) !== null)?.currency || 'USD';
          const originalTotal = Number(item?.total?.original ?? 0) + passengerExtraTotal;
          const finalTotal = Number(item?.total?.final ?? 0) + passengerExtraTotal;

          return (
            <Accordion.Item eventKey={idx.toString()} key={idx}>
              <Accordion.Header>
                <div className='acc-passenger-header'>
                  <UserIcon />
                  <PassengerEnglishName lastName={item.lastName} firstName={item.firstName} fallbackName={item.name} />
                  <span className='header-price'>${finalTotal.toFixed(2)}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <div className='passenger'>
                  <div className='passenger-info'>
                    <p className='passenger-name'>생년월일<span>{item.birth}</span></p>
                    <p className='passenger-gender'>성별<span>{item.gender}</span></p>
                    <p className='passenger-tel'>휴대폰 번호<span>{item.phone ? item.phone : '-'}</span></p>
                  </div>

                  <ul className='passenger-price'>
                    <li>
                      <p className='title'>선실 요금 :</p>
                      <div className='content'>
                        <span className='line-cancel original-price'>${item.fare.original.toFixed(2)}</span>
                        <span className='discount-price'>${item.fare.discounted.toFixed(2)}</span>
                        {/* <span className='other-price'>(CCF: ${item.fare.breakdown.CCF.toFixed(2)} / NCCF: ${item.fare.breakdown.NCCF.toFixed(2)})</span> */}
                      </div>
                    </li>

                    <li>
                      <p className='title'>세금 :</p> 
                      <div className='content'>
                        <span className='tax-price'>${item.tax.toFixed(2)}</span>
                      </div>
                    </li>

                    <li>
                      <p className='title'>선상팁 :</p>
                      <div className='content'>
                        <span className='tip-price'>${item.gratuity.toFixed(2)}</span>
                      </div>
                    </li>

                    {passengerExtras.length > 0 && (
                      <li>
                        <p className='title'>부가 서비스 :</p>
                        <div className='content'>
                          <span className='extra-price'>{formatCurrency(passengerExtraTotal, passengerExtraCurrency)}</span>
                        </div>
                      </li>
                    )}

                    <li>
                      <p className='title total'>총계 :</p>
                      <div className='content'>
                        <span className='line-cancel original-total-price'>${originalTotal.toFixed(2)}</span>
                        <span className='discount-total-price'>${finalTotal.toFixed(2)}</span>
                      </div>
                    </li>

                    {/* <li>
                      <span className='total-save-price'>
                        (EXTRA SAVINGS: -${item.savings.extra.toFixed(2)} / 50% OFF REF: -${item.savings.refDiscount.toFixed(2)} / 싱글차지: ${item.savings.singleSurcharge.toFixed(2)})
                      </span>
                    </li> */}
                  </ul>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          );
        }) : (
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <div className='acc-passenger-header'><UserIcon />승객 정보 없음<span className='header-price'>-</span></div>
            </Accordion.Header>
            <Accordion.Body>
              <p>승객 정보를 불러올 수 없습니다. (데이터 형식 오류 또는 데이터 없음)</p>
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>
      
      {(promotions.length > 0 || extras.length > 0) && (
        <div className='payment-summary'>
          {promotions.length > 0 && (
            <>
              <p className='payment-summary-title'>프로모션</p>
              {promotions.map((promo, idx) => (
                <p className='payment-summary-row' key={`promo-${idx}`}>
                  <span className='payment-summary-code'>{promo.code}</span>
                  {promo.description ? ` - ${promo.description}` : ''}
                </p>
              ))}
            </>
          )}
          {extras.length > 0 && (
            <>
              <p className='payment-summary-title'>부가서비스</p>
              {extras.map((extra, idx) => {
                const codeLabel = extra?.code ? String(extra.code).trim() : '';
                const description = extra?.description ? String(extra.description).trim() : '';
                const fallbackLabel = getExtraLabel(extra) || '부가서비스';
                const passengerName = getExtraPassengerName(extra);
                const amount = parseAmount(extra?.price);

                return (
                  <p className='payment-summary-row' key={`extra-${idx}`}>
                    {codeLabel ? (
                      <>
                        <span className='payment-summary-code'>{codeLabel}</span>
                        {description ? ` - ${description}` : ''}
                      </>
                    ) : fallbackLabel}
                    {amount !== null ? ` (${formatCurrency(amount, extra?.currency || 'USD')})` : ''}
                    {passengerName ? <span className='payment-summary-passenger'>{passengerName}</span> : null}
                  </p>
                );
              })}
            </>
          )}
        </div>
      )}
      <PromotionDiscountRows
        paymentSummary={paymentSummary}
        formatCurrency={formatCurrency}
      />
      <h3 className='all-passenger-total-price'>총 합계<span>{formatCurrency(summaryTotal)}</span></h3>
      {Array.isArray(onboardCredit?.items) && onboardCredit.items.length > 0 && (
        <div className='onboard-credit-summary'>
          <div className='onboard-credit-head'>
            <span className='onboard-credit-title'>{onboardCredit?.title || '특별 혜택'}</span>
            <span className='onboard-credit-note'>{onboardCredit?.note || '선내에서 사용 가능한 무료 리워드 크레딧'}</span>
          </div>
          {onboardCredit.items.map((item, idx) => {
            const description = String(item?.description || 'On-Board Credit').toUpperCase();
            const amountLabel = item?.amountLabel || formatCurrency(item?.amount ?? 0);
            return (
              <div key={`onboard-credit-${idx}`} className='onboard-credit-row'>
                <span className='onboard-credit-description'>
                  {description}
                  {item?.guestNo && (item?.lastName || item?.firstName) ? (
                    <span className='payment-summary-passenger'>
                      {item.guestNo}번째 승객 · {[item.lastName, item.firstName].filter(Boolean).join(' ')}
                    </span>
                  ) : null}
                </span>
                <span className='onboard-credit-amount'>{amountLabel}</span>
              </div>
            );
          })}
          {onboardCredit.items.length > 1
            && onboardCredit.items.every(item => item.guestNo > 0 && item.currency === onboardCredit.currency)
            && Number.isFinite(onboardCredit.totalAmount) && onboardCredit.totalAmount > 0 && (
              <div className='onboard-credit-row'>
                <span className='onboard-credit-description'>총 온보드 크레딧</span>
                <span className='onboard-credit-amount'>{formatCurrency(onboardCredit.totalAmount, onboardCredit.currency)}</span>
              </div>
            )}
        </div>
      )}
      {showPaymentSummary && showBreakdown && (
        <div className='payment-breakdown'>
          <div className='payment-line'>
            <span className='label'>예약금</span>
            <span className='amount'>{formatCurrency(displayDeposit)}</span>
            <span className='status'>{depositStatusLabel}</span>
          </div>
          <div className='payment-line'>
            <span className='label'>잔금</span>
            <span className='amount'>{finalAmountLabel}</span>
            <span className='status'>{finalStatusLabel}</span>
          </div>
        </div>
      )}
      {showPaymentSummary && visiblePaymentReceiptsForView.length > 0 && (
        <div className='payment-history'>
          <div className='payment-history-title'>결제 내역</div>
          <table className='payment-history-table'>
            <thead>
              <tr>
                <th>결제일</th>
                <th>결제금액</th>
                <th>항목</th>
                <th>결제수단</th>
              </tr>
            </thead>
            <tbody>
              {visiblePaymentReceiptsForView.map((receipt, idx) => {
                const paidDate = receipt?.paid_date || (receipt?.paid_at ? String(receipt.paid_at).slice(0, 10) : '');
                const amountVal = parseAmount(receipt?.payment_amount_usd_adjusted ?? receipt?.payment_amount_usd);
                const paymentType = String(receipt?.payment_type || '').toUpperCase();
                const itemLabel = paymentType === 'D'
                  ? '예약금'
                  : paymentType === 'F'
                    ? '잔금'
                    : paymentType === 'T'
                      ? '전액'
                      : paymentType === 'R'
                        ? '환불'
                        : '-';
                const cardType = receipt?.card_type || '';
                const cardMasked = receipt?.card_number_masked || '';
                const cardLabel = cardType && cardType !== 'UNKNOWN'
                  ? [cardType, cardMasked].filter(Boolean).join(' ')
                  : '-';
                return (
                  <tr key={`receipt-${idx}`}>
                    <td>{paidDate || '-'}</td>
                    <td>{amountVal !== null ? formatCurrency(amountVal) : '-'}</td>
                    <td>{itemLabel}</td>
                    <td>{cardLabel}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default NavPassengerContent;
